import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Switch,
  Stack,
  CircularProgress,
  Tabs,
  Tab,
  Popover,
  Paper,
  Grid,
  Tooltip,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Plus, Pencil, Trash2, Palette, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { departmentService, userService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { User, UserRole } from '@/types';
import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import { isSuperAdmin } from '@/shared/utils/roles';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import PermissionsManager from '../components/PermissionsManager';

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: UserRole.SUPER_ADMIN, label: 'Super Admin' },
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.MANAGER, label: 'Manager' },
  { value: UserRole.DEPARTMENT_HEAD, label: 'Department Head' },
  { value: UserRole.PROJECT_MANAGER, label: 'Project Manager' },
  { value: UserRole.TEAM_LEAD, label: 'Team Lead' },
  { value: UserRole.ENGINEER, label: 'Engineer' },
  { value: UserRole.INDIVIDUAL_CONTRIBUTOR, label: 'Individual Contributor' },
];

const DEFAULT_COLORS = [
  { hex: '#3f51b5', name: 'Indigo' },
  { hex: '#2196f3', name: 'Blue' },
  { hex: '#00bcd4', name: 'Cyan' },
  { hex: '#009688', name: 'Teal' },
  { hex: '#4caf50', name: 'Green' },
  { hex: '#ff9800', name: 'Orange' },
  { hex: '#f44336', name: 'Red' },
  { hex: '#9c27b0', name: 'Purple' },
  { hex: '#e91e63', name: 'Pink' },
  { hex: '#607d8b', name: 'Blue Grey' },
];

const buildDisplayName = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`.trim();

const UsersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const currentUser = useAppSelector(selectCurrentUser);
  const isSuperAdminUser = isSuperAdmin(currentUser?.role);
  const { showError, showSuccess } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  // Department-related state
  const [deptCreateOpen, setDeptCreateOpen] = useState(false);
  const [deptEditOpen, setDeptEditOpen] = useState(false);
  const [deptDeleteOpen, setDeptDeleteOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState<any | null>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
  const [customColorMode, setCustomColorMode] = useState(false);
  const [activeColorField, setActiveColorField] = useState<'create' | 'edit' | null>(null);
  const [deptForm, setDeptForm] = useState({
    name: '',
    headId: '',
    color: '',
  });
  const [deptEditForm, setDeptEditForm] = useState({
    name: '',
    headId: '',
    color: '',
  });

  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    displayName: '',
    role: UserRole.ENGINEER as UserRole,
    departmentId: '',
    isActive: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    role: UserRole.ENGINEER as UserRole,
    departmentId: '',
    isActive: true,
  });

  const { data: departments = [] } = useQuery({
    queryKey: QUERY_KEYS.departments,
    queryFn: () => departmentService.getDepartments(),
  });

  const { data: users = [], isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.users, search, statusFilter],
    queryFn: () => {
      const term = search.trim();
      if (term.length >= 2) {
        return userService.searchUsers(term);
      }
      return userService.getUsers(statusFilter);
    },
    staleTime: 30000,
  });

  const departmentMap = useMemo(() => {
    const map = new Map<string, string>();
    departments.forEach((dept) => map.set(dept.id, dept.name));
    return map;
  }, [departments]);

  const createUserMutation = useMutation({
    mutationFn: (payload: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      displayName?: string;
      role: string;
      departmentId: string;
      isActive?: boolean;
    }) => userService.createUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      setCreateOpen(false);
      setShowPassword(false);
      setPasswordErrors([]);
      setForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        displayName: '',
        role: UserRole.ENGINEER,
        departmentId: '',
        isActive: true,
      });
      showSuccess('User created successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to create user';
      showError(message);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: {
      role?: UserRole;
      isActive?: boolean;
      firstName?: string;
      lastName?: string;
      displayName?: string;
      departmentId?: string;
    } }) => userService.updateUser(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      setUpdatingUserId(null);
      setEditOpen(false);
      setSelectedUser(null);
    },
    onError: () => {
      setUpdatingUserId(null);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      setDeleteOpen(false);
      setSelectedUser(null);
    },
  });

  // Department mutations
  const createDeptMutation = useMutation({
    mutationFn: (payload: { name: string; headId?: string; color?: string }) =>
      departmentService.createDepartment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments });
      setDeptCreateOpen(false);
      setDeptForm({ name: '', headId: '', color: '' });
    },
  });

  const updateDeptMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<{ name: string; headId: string; color: string }> }) =>
      departmentService.updateDepartment(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments });
      setDeptEditOpen(false);
      setSelectedDept(null);
    },
  });

  const deleteDeptMutation = useMutation({
    mutationFn: (deptId: string) => departmentService.deleteDepartment(deptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments });
      setDeptDeleteOpen(false);
      setSelectedDept(null);
    },
  });

  const handleDeptEditOpen = (dept: any) => {
    setSelectedDept(dept);
    setDeptEditForm({
      name: dept.name,
      headId: dept.headId || '',
      color: dept.color || '',
    });
    setDeptEditOpen(true);
  };

  const handleDeptDeleteOpen = (dept: any) => {
    setSelectedDept(dept);
    setDeptDeleteOpen(true);
  };

  const handleDeptCreate = () => {
    createDeptMutation.mutate({
      name: deptForm.name.trim(),
      headId: deptForm.headId || undefined,
      color: deptForm.color || undefined,
    });
  };

  const handleDeptUpdate = () => {
    if (!selectedDept) return;
    updateDeptMutation.mutate({
      id: selectedDept.id,
      payload: {
        name: deptEditForm.name.trim(),
        headId: deptEditForm.headId || undefined,
        color: deptEditForm.color || undefined,
      },
    });
  };

  // Password validation function
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 12) {
      errors.push('Password must be at least 12 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Must contain at least one number');
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      errors.push('Must contain at least one special character');
    }
    
    return errors;
  };

  const handlePasswordChange = (password: string) => {
    setForm((prev) => ({ ...prev, password }));
    const errors = validatePassword(password);
    setPasswordErrors(errors);
  };

  const handleCloseCreateDialog = () => {
    setCreateOpen(false);
    setShowPassword(false);
    setPasswordErrors([]);
  };

  const handleCreateUser = () => {
    // Validate password before submitting
    const errors = validatePassword(form.password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    const displayName = form.displayName.trim() || buildDisplayName(form.firstName, form.lastName);
    createUserMutation.mutate({
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      displayName,
      role: form.role,
      departmentId: form.departmentId,
      isActive: form.isActive,
    });
  };

  const handleEditOpen = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      displayName: user.displayName ?? '',
      role: user.role,
      departmentId: user.departmentId ?? '',
      isActive: user.isActive,
    });
    setEditOpen(true);
  };

  const handleDeleteOpen = (user: User) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleEditSave = () => {
    if (!selectedUser) return;
    const displayName = editForm.displayName.trim() || buildDisplayName(editForm.firstName, editForm.lastName);
    updateUserMutation.mutate({
      id: selectedUser.id,
      payload: {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        displayName,
        role: editForm.role,
        departmentId: editForm.departmentId,
        isActive: editForm.isActive,
      },
    });
  };

  return (
    <Box>
      {activeTab !== 2 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => (activeTab === 0 ? setCreateOpen(true) : setDeptCreateOpen(true))}
            fullWidth={isMobile}
          >
            {activeTab === 0 ? 'Add User' : 'Add Department'}
          </Button>
        </Box>
      )}

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => setActiveTab(newVal)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: { xs: 1, sm: 2 },
            '& .MuiTab-root': {
              minWidth: isMobile ? 'auto' : 120,
              px: isMobile ? 1.5 : 2,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            },
          }}
        >
          <Tab label="Users" />
          <Tab label="Departments" />
          {isSuperAdminUser && <Tab label="Permissions" />}
        </Tabs>
        <CardContent sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 2, sm: 2.5 } }}>
          {activeTab === 0 ? (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 152px' },
                  gap: 2,
                  mb: 2,
                  alignItems: 'start',
                }}
              >
                <TextField
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  label="Search users"
                  placeholder="Search by name or email"
                  size="small"
                  fullWidth
                />
                <AppSelect
                  id="user-status-filter"
                  label="Status"
                  size="small"
                  fullWidth={false}
                  sx={{ width: { xs: '100%', sm: 152 } }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'active' | 'inactive' | 'all')}
                >
                  <AppMenuItem value="active">Active</AppMenuItem>
                  <AppMenuItem value="inactive">Inactive</AppMenuItem>
                  <AppMenuItem value="all">All</AppMenuItem>
                </AppSelect>
              </Box>

              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.displayName || buildDisplayName(user.firstName, user.lastName)}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{departmentMap.get(user.departmentId) ?? user.departmentId}</TableCell>
                      <TableCell>
                        <Chip
                          label={ROLE_OPTIONS.find((r) => r.value === user.role)?.label || user.role}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          size="small"
                          variant={user.isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                          flexWrap="nowrap"
                        >
                          {isMobile ? (
                            <>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleEditOpen(user)} aria-label="Edit user">
                                  <Pencil size={16} />
                                </IconButton>
                              </Tooltip>
                              {user.isActive ? (
                                <Tooltip title="Deactivate">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteOpen(user)}
                                    aria-label="Deactivate user"
                                  >
                                    <Trash2 size={16} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Reactivate">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => {
                                      updateUserMutation.mutate({
                                        id: user.id,
                                        payload: { isActive: true },
                                      });
                                    }}
                                    disabled={updateUserMutation.isPending}
                                    aria-label="Reactivate user"
                                  >
                                    <RefreshCw size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Pencil size={14} />}
                                onClick={() => handleEditOpen(user)}
                              >
                                Edit
                              </Button>
                              {user.isActive ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<Trash2 size={14} />}
                                  onClick={() => handleDeleteOpen(user)}
                                >
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<RefreshCw size={14} />}
                                  onClick={() => {
                                    updateUserMutation.mutate({
                                      id: user.id,
                                      payload: { isActive: true },
                                    });
                                  }}
                                  disabled={updateUserMutation.isPending}
                                >
                                  Reactivate
                                </Button>
                              )}
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
            </>
          ) : activeTab === 1 ? (
            <>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Department Name</TableCell>
                      <TableCell>Head</TableCell>
                      <TableCell>Teams</TableCell>
                      <TableCell>Members</TableCell>
                      <TableCell>Active Projects</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departments.map((dept: any) => (
                      <TableRow key={dept.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {dept.color && (
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: dept.color }} />
                            )}
                            <Typography variant="body2" fontWeight={500}>{dept.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{dept.headName || '-'}</TableCell>
                        <TableCell>{dept.teamCount || 0}</TableCell>
                        <TableCell>{dept.memberCount || 0}</TableCell>
                        <TableCell>{dept.activeProjects || 0}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {isMobile ? (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleDeptEditOpen(dept)} aria-label="Edit department">
                                    <Pencil size={16} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeptDeleteOpen(dept)}
                                    aria-label="Delete department"
                                  >
                                    <Trash2 size={16} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Pencil size={14} />}
                                  onClick={() => handleDeptEditOpen(dept)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<Trash2 size={14} />}
                                  onClick={() => handleDeptDeleteOpen(dept)}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {departments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No departments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <PermissionsManager embedded />
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(event) => handlePasswordChange(event.target.value)}
              fullWidth
              required
              error={passwordErrors.length > 0}
              helperText={
                passwordErrors.length > 0 
                  ? passwordErrors.join(', ')
                  : 'Must be at least 12 characters with uppercase, lowercase, number, and special character'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="First Name"
              value={form.firstName}
              onChange={(event) => setForm((prev) => ({ ...prev, firstName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={form.lastName}
              onChange={(event) => setForm((prev) => ({ ...prev, lastName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Display Name"
              value={form.displayName}
              onChange={(event) => setForm((prev) => ({ ...prev, displayName: event.target.value }))}
              fullWidth
              helperText="Optional. Defaults to first and last name."
            />
            <AppSelect
              id="user-role"
              label="Role"
              autoWidth
              value={form.role}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, role: event.target.value as UserRole }))
              }
            >
              {ROLE_OPTIONS.map((role) => (
                <AppMenuItem key={role.value} value={role.value}>
                  {role.label}
                </AppMenuItem>
              ))}
            </AppSelect>
            <AppSelect
              id="user-dept"
              label="Department"
              required
              autoWidth
              value={form.departmentId}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, departmentId: event.target.value as string }))
              }
            >
              {departments.map((dept) => (
                <AppMenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </AppMenuItem>
              ))}
            </AppSelect>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCreateDialog} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={createUserMutation.isPending || passwordErrors.length > 0}
          >
            {createUserMutation.isPending ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="First Name"
              value={editForm.firstName}
              onChange={(event) => setEditForm((prev) => ({ ...prev, firstName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={editForm.lastName}
              onChange={(event) => setEditForm((prev) => ({ ...prev, lastName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Display Name"
              value={editForm.displayName}
              onChange={(event) => setEditForm((prev) => ({ ...prev, displayName: event.target.value }))}
              fullWidth
              helperText="Optional. Defaults to first and last name."
            />
            <AppSelect
              id="edit-user-role"
              label="Role"
              autoWidth
              value={editForm.role}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, role: event.target.value as UserRole }))
              }
            >
              {ROLE_OPTIONS.map((role) => (
                <AppMenuItem key={role.value} value={role.value}>
                  {role.label}
                </AppMenuItem>
              ))}
            </AppSelect>
            <AppSelect
              id="edit-user-dept"
              label="Department"
              required
              autoWidth
              value={editForm.departmentId}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, departmentId: event.target.value as string }))
              }
            >
              {departments.map((dept) => (
                <AppMenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </AppMenuItem>
              ))}
            </AppSelect>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={updateUserMutation.isPending}
          >
            {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate {selectedUser?.displayName || selectedUser?.email}? The user will not be able to log in, but their data will be preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedUser && deleteUserMutation.mutate(selectedUser.id)}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Department Dialogs */}
      <Dialog open={deptCreateOpen} onClose={() => setDeptCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Department</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Department Name"
              value={deptForm.name}
              onChange={(e) => setDeptForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <AppSelect
              id="dept-head"
              label="Department Head"
              autoWidth
              value={deptForm.headId}
              onChange={(e) => setDeptForm(prev => ({ ...prev, headId: e.target.value as string }))}
            >
              <AppMenuItem value="">None</AppMenuItem>
              {users.map((user) => (
                <AppMenuItem key={user.id} value={user.id}>
                  {user.displayName || user.email}
                </AppMenuItem>
              ))}
            </AppSelect>
            {/* Color Picker */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Department Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: deptForm.color || '#e0e0e0',
                    border: '2px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={(e) => {
                    setColorPickerAnchor(e.currentTarget);
                    setActiveColorField('create');
                    setCustomColorMode(false);
                  }}
                />
                <TextField
                  size="small"
                  value={deptForm.color}
                  onChange={(e) => setDeptForm(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#3f51b5"
                  sx={{ flex: 1 }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Button
                        size="small"
                        startIcon={<Palette size={16} />}
                        onClick={(e) => {
                          setColorPickerAnchor(e.currentTarget);
                          setActiveColorField('create');
                          setCustomColorMode(false);
                        }}
                      >
                        Pick
                      </Button>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeptCreateOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDeptCreate}
            disabled={createDeptMutation.isPending || !deptForm.name.trim()}
          >
            {createDeptMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deptEditOpen} onClose={() => setDeptEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Department</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Department Name"
              value={deptEditForm.name}
              onChange={(e) => setDeptEditForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <AppSelect
              id="dept-head-edit"
              label="Department Head"
              autoWidth
              value={deptEditForm.headId}
              onChange={(e) => setDeptEditForm(prev => ({ ...prev, headId: e.target.value as string }))}
            >
              <AppMenuItem value="">None</AppMenuItem>
              {users.map((user) => (
                <AppMenuItem key={user.id} value={user.id}>
                  {user.displayName || user.email}
                </AppMenuItem>
              ))}
            </AppSelect>
            {/* Color Picker */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Department Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: deptEditForm.color || '#e0e0e0',
                    border: '2px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={(e) => {
                    setColorPickerAnchor(e.currentTarget);
                    setActiveColorField('edit');
                    setCustomColorMode(false);
                  }}
                />
                <TextField
                  size="small"
                  value={deptEditForm.color}
                  onChange={(e) => setDeptEditForm(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#3f51b5"
                  sx={{ flex: 1 }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Button
                        size="small"
                        startIcon={<Palette size={16} />}
                        onClick={(e) => {
                          setColorPickerAnchor(e.currentTarget);
                          setActiveColorField('edit');
                          setCustomColorMode(false);
                        }}
                      >
                        Pick
                      </Button>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeptEditOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDeptUpdate}
            disabled={updateDeptMutation.isPending || !deptEditForm.name.trim()}
          >
            {updateDeptMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deptDeleteOpen} onClose={() => setDeptDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Department</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedDept?.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeptDeleteOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedDept && deleteDeptMutation.mutate(selectedDept.id)}
            disabled={deleteDeptMutation.isPending}
          >
            {deleteDeptMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={() => {
          setColorPickerAnchor(null);
          setActiveColorField(null);
          setCustomColorMode(false);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 2, width: 280 }}>
          {!customColorMode ? (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Preset Colors
              </Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {DEFAULT_COLORS.map((color) => (
                  <Grid item xs={4} key={color.hex}>
                    <Tooltip title={color.name}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 50,
                          borderRadius: 1,
                          bgcolor: color.hex,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor:
                            (activeColorField === 'create' && deptForm.color === color.hex) ||
                            (activeColorField === 'edit' && deptEditForm.color === color.hex)
                              ? 'primary.main'
                              : 'transparent',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 2,
                          },
                          transition: 'all 0.2s',
                        }}
                        onClick={() => {
                          if (activeColorField === 'create') {
                            setDeptForm(prev => ({ ...prev, color: color.hex }));
                          } else if (activeColorField === 'edit') {
                            setDeptEditForm(prev => ({ ...prev, color: color.hex }));
                          }
                          setColorPickerAnchor(null);
                          setActiveColorField(null);
                        }}
                      />
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Palette size={16} />}
                onClick={() => setCustomColorMode(true)}
              >
                Custom Color
              </Button>
            </>
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Custom Color
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Hex Color"
                  value={activeColorField === 'create' ? deptForm.color : deptEditForm.color}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (activeColorField === 'create') {
                      setDeptForm(prev => ({ ...prev, color: value }));
                    } else if (activeColorField === 'edit') {
                      setDeptEditForm(prev => ({ ...prev, color: value }));
                    }
                  }}
                  placeholder="#3f51b5"
                  fullWidth
                  size="small"
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <input
                    type="color"
                    value={activeColorField === 'create' ? deptForm.color || '#3f51b5' : deptEditForm.color || '#3f51b5'}
                    onChange={(e) => {
                      if (activeColorField === 'create') {
                        setDeptForm(prev => ({ ...prev, color: e.target.value }));
                      } else if (activeColorField === 'edit') {
                        setDeptEditForm(prev => ({ ...prev, color: e.target.value }));
                      }
                    }}
                    style={{
                      width: '100%',
                      height: 80,
                      border: '2px solid #e0e0e0',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setCustomColorMode(false)}
                    fullWidth
                  >
                    Back
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      setColorPickerAnchor(null);
                      setActiveColorField(null);
                      setCustomColorMode(false);
                    }}
                    fullWidth
                  >
                    Done
                  </Button>
                </Box>
              </Stack>
            </>
          )}
        </Paper>
      </Popover>
    </Box>
  );
};

export default UsersPage;
